import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './icon';
import './select-field.scss';

function nextEnabled(options, from, step) {
  const count = options.length;
  if (!count) return -1;
  for (let offset = 1; offset <= count; offset += 1) {
    const index = (from + step * offset + count * 2) % count;
    if (!options[index].disabled) return index;
  }
  return from;
}

function measureMenu(button) {
  const rect = button.getBoundingClientRect();
  const maxHeight = 320;
  const spaceBelow = window.innerHeight - rect.bottom - 8;
  const spaceAbove = rect.top - 8;
  const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
  const room = Math.max(80, Math.min(maxHeight, openUp ? spaceAbove : spaceBelow));
  return {
    left: rect.left,
    width: rect.width,
    maxHeight: room,
    top: openUp ? 'auto' : rect.bottom + 4,
    bottom: openUp ? window.innerHeight - rect.top + 4 : 'auto',
  };
}

function SelectField({
  label,
  value,
  onChange,
  options,
  required = true,
  clearable = false,
  disabled = false,
  searchable = false,
  onSearch,
}) {
  const labelId = useId();
  const listId = useId();
  const rootRef = useRef(null);
  const controlRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const [menuStyle, setMenuStyle] = useState(null);
  const [query, setQuery] = useState('');
  const selected = options.find((option) => option.value === value);
  const visible = searchable && !onSearch && query.trim()
    ? options.filter((option) => {
      const hay = `${option.label || ''} ${option.note || ''} ${option.badge || ''}`.toLowerCase();
      return hay.includes(query.trim().toLowerCase());
    })
    : options;
  const raised = open || focused || value !== '';
  const className = [
    'text-field',
    'select-field',
    raised ? 'is-raised' : '',
    !disabled && (open || focused) ? 'is-focus' : '',
    disabled ? 'is-readonly' : '',
  ].filter(Boolean).join(' ');

  function openMenu(start) {
    const index = start >= 0 && !options[start]?.disabled
      ? start
      : nextEnabled(options, start >= 0 ? start : -1, 1);
    setCursor(index);
    setQuery('');
    if (onSearch) onSearch('');
    const anchor = controlRef.current || buttonRef.current;
    if (anchor) setMenuStyle(measureMenu(anchor));
    setOpen(true);
  }

  function choose(option) {
    if (!option || option.disabled) return;
    onChange(option.value);
    setOpen(false);
    buttonRef.current?.focus();
  }

  function toggleMenu() {
    if (disabled) return;
    if (open) {
      setOpen(false);
      return;
    }
    openMenu(options.findIndex((option) => option.value === value));
  }

  function clearValue() {
    onChange('');
    setOpen(false);
    buttonRef.current?.focus();
  }

  function handleControlMouseDown(event) {
    if (event.button !== 0) return;
    if (event.target.closest('.text-field__action')) return;
    if (event.target.closest('.select-field__trigger')) return;
    event.preventDefault();
    buttonRef.current?.focus();
  }

  function handleControlClick(event) {
    if (event.target.closest('.text-field__action')) return;
    if (event.target.closest('.select-field__trigger')) return;
    toggleMenu();
  }

  function handleKeyDown(event) {
    if (disabled) return;
    const current = options.findIndex((option) => option.value === value);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowUp' ? -1 : 1;
      if (!open) {
        openMenu(current >= 0 ? current : (step < 0 ? options.length : -1));
        return;
      }
      setCursor((index) => nextEnabled(options, index, step));
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!open) {
        openMenu(current);
        return;
      }
      choose(options[cursor]);
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
    if (event.key === 'Tab') setOpen(false);
  }

  useLayoutEffect(() => {
    if (!open || !controlRef.current) return undefined;
    function place() {
      setMenuStyle(measureMenu(controlRef.current));
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event) {
      if (rootRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open || cursor < 0) return;
    document.getElementById(`${listId}-option-${cursor}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, cursor, listId]);

  return (
    <div className={className} ref={rootRef}>
      <div
        ref={controlRef}
        className="text-field__control"
        onMouseDown={handleControlMouseDown}
        onClick={handleControlClick}
      >
        <button
          ref={buttonRef}
          type="button"
          className="select-field__trigger"
          aria-labelledby={labelId}
          aria-haspopup="listbox"
          aria-disabled={disabled}
          disabled={disabled}
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open && cursor >= 0 ? `${listId}-option-${cursor}` : undefined}
          onClick={toggleMenu}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        >
          <span className="text-field__body">
            <span className="text-field__caption" id={labelId}>
              <span>{label}</span>
              {required && !disabled ? <span className="text-field__mark" aria-hidden="true">*</span> : null}
            </span>
            <span className="text-field__input">{selected ? selected.label : ''}</span>
          </span>
        </button>
        <span className="text-field__actions">
          {clearable && value !== '' ? (
            <button
              type="button"
              className="text-field__action"
              aria-label="Очистить"
              onMouseDown={(event) => event.preventDefault()}
              onClick={clearValue}
            >
              <span className="text-field__icon text-field__icon--clear" />
            </button>
          ) : null}
          <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
        </span>
      </div>
      {open ? createPortal(
        <div
          ref={menuRef}
          className="select-field__menu"
          id={listId}
          role="listbox"
          aria-labelledby={labelId}
          style={menuStyle || undefined}
        >
          {searchable ? (
            <div className="select-field__search">
              <Icon name="search" size={16} />
              <input
                type="search"
                placeholder="Поиск"
                value={query}
                onChange={(event) => {
                  const next = event.target.value;
                  setQuery(next);
                  if (onSearch) onSearch(next);
                }}
                onMouseDown={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              />
            </div>
          ) : null}
          {visible.map((option, index) => {
            const current = option.value === value;
            const active = index === cursor;
            return (
              <button
                key={option.value || 'none'}
                id={`${listId}-option-${index}`}
                type="button"
                role="option"
                className={[
                  'select-field__option',
                  current ? 'is-selected' : '',
                  active ? 'is-cursor' : '',
                ].filter(Boolean).join(' ')}
                aria-selected={current}
                disabled={option.disabled}
                onMouseEnter={() => {
                  if (!option.disabled) setCursor(index);
                }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(option)}
              >
                <span className="select-field__option-copy">
                  <span className="select-field__option-label">{option.label}</span>
                  {option.note || option.badge ? (
                    <span className="select-field__option-note">
                      {option.note}
                      {option.badge ? <em className="select-field__badge">{option.badge}</em> : null}
                    </span>
                  ) : null}
                </span>
                {current ? (
                  <span className="select-field__check">
                    <Icon name="check" size={16} tone="inherit" />
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}

export default SelectField;
